import { ActionFunction, LoaderFunctionArgs, redirect } from "react-router";
import { LoaderFunction, useRouteLoaderData } from "react-router-typesafe";
import { runGqlMutation, runGqlQuery } from "gqlClient";
import {
  ApproveScriptDocument,
  ApproveScriptMutationVariables,
  LensScriptFlag,
  ListScriptRunsDocument,
  ListScriptsDocument,
  UpsertScriptDocument,
  UpsertScriptMutationVariables,
} from "generated/graphql";
import { array, enum_, number, object, optional, parse, picklist, string } from "valibot";
import { parseSafeInt } from "Backend";
import store from "redux/store";
import { addLoaderError } from "redux/slices/loader-error.slice";
import { fetchCustomProperties } from "redux/slices/lensEditor";

export const scriptsLoader = ((args: LoaderFunctionArgs) => {
  const lensId = parseSafeInt(args.params.lensId);

  if (lensId === undefined) {
    return redirect("..");
  }

  return Promise.all([
    runGqlQuery(ListScriptsDocument, {
      lensId,
    }).then((r) => r.lens.scripts.listScripts),
    store.dispatch(fetchCustomProperties({ lensId })),
  ])
    .then(([scripts]) => scripts.map((s) => ({ ...s, expectedCalcKeys: [] })))
    .catch((e) => {
      store.dispatch(
        addLoaderError({
          loaderName: "scriptsLoader",
          message: "Unable to load scripts",
        })
      );
      console.error(e);
      return [];
    });
}) satisfies LoaderFunction;

export const useScriptsLoader = () => {
  return useRouteLoaderData<typeof scriptsLoader>("lens-editor/lens/scripts");
};

export const useScriptNameLoader = (scriptName: string | undefined) => {
  const scripts = useScriptsLoader();
  return scripts.find((s) => s.name === scriptName);
};

export const scriptsHistoryLoader = ((args: LoaderFunctionArgs) => {
  const lensId = parseInt(args.params.lensId || "");
  const searchParams = new URL(args.request.url).searchParams;
  const caseId = searchParams.get("caseId");
  const minDate = searchParams.get("minDate");
  const maxDate = searchParams.get("maxDate");

  if (isNaN(lensId)) {
    return redirect("..");
  }

  return runGqlQuery(ListScriptRunsDocument, {
    lensId,
    caseId: parseSafeInt(caseId),
    minDate,
    maxDate,
    offset: 0,
    limit: 1000,
  })
    .then((r) => r.lens.scripts.listScriptRuns)
    .catch((e) => {
      console.error(e);
      store.dispatch(
        addLoaderError({
          loaderName: "scriptsHistoryLoader",
          message: "Unable to load Script history",
        })
      );
      return [];
    });
}) satisfies LoaderFunction;

export const useScriptsHistoryLoader = () =>
  useRouteLoaderData<typeof scriptsHistoryLoader>("lens-editor/lens/scripts-history");

interface FetcherError {
  error: any;
}

const ScriptActionSchema = object({
  intent: picklist(["upsert", "approve"]),
  lensId: number(),
  scriptName: string(),
  scriptContent: optional(string()),
  approvalSecret: optional(string()),
  flags: optional(array(enum_(LensScriptFlag))),
  requiredFieldFindings: optional(array(number())),
  defaultParameters: optional(
    array(
      object({
        key: string(),
        value: string(),
      })
    )
  ),
});

const upsertScript = async (script: UpsertScriptMutationVariables): Promise<void | FetcherError> => {
  try {
    return await runGqlMutation(UpsertScriptDocument, script).then((r) => r.lens.scripts.upsertScript);
  } catch (e) {
    return { error: e };
  }
};

const approveScript = async (script: ApproveScriptMutationVariables): Promise<void | FetcherError> => {
  try {
    return await runGqlMutation(ApproveScriptDocument, script).then((r) => r.lens.scripts.approveScript);
  } catch (e) {
    return { error: e };
  }
};

export const scriptsAction: ActionFunction = async ({ request }) => {
  const scriptActionRequest = await request.json();
  const action = parse(ScriptActionSchema, scriptActionRequest);

  if (
    action.intent === "upsert" &&
    action.scriptContent !== undefined &&
    action.flags !== undefined &&
    action.requiredFieldFindings !== undefined &&
    action.defaultParameters !== undefined
  ) {
    return upsertScript({
      lensId: action.lensId,
      scriptName: action.scriptName,
      scriptContent: action.scriptContent,
      flags: action.flags,
      requiredFieldFindings: action.requiredFieldFindings,
      defaultParameters: action.defaultParameters,
    });
  } else if (action.intent === "approve" && action.approvalSecret) {
    return approveScript({
      lensId: action.lensId,
      approvalSecret: action.approvalSecret,
      scriptName: action.scriptName,
    });
  }
};
